import { useCallback, useState } from 'react';
import {
  setScanIntentToken,
  setScanIntentId,
  trackScanStarted,
  trackLeadSubmitted,
  readClickId,
} from '@privisy/ui-bridge';
import { startAnonymousAudit, startLiteScan } from '../utils/api';
import { navigation } from '../utils/signInRedirect';

/**
 * The claim URL the www hero sends a finished scan to (LiteScanWidget's
 * buildClaimUrl), on this origin: the claim page reads `domain` from it.
 */
function claimUrl(scanId: string, intentToken: string, target: string): string {
  const url = new URL(window.location.pathname, window.location.origin);
  url.searchParams.set('claimScanId', scanId);
  url.searchParams.set('intentToken', intentToken);
  try {
    url.searchParams.set('domain', new URL(target).hostname);
  } catch { /* validated upstream; a bad URL just loses the prefill */ }
  url.hash = `/scan/${scanId}`;
  return url.toString();
}

/**
 * Starts an anonymous scan and hands it to the claim flow: the intent token
 * and scanId go into the cross-subdomain cookies (so useClaimScan can attach
 * the scan after sign-up, even across the email-verification redirect that
 * returns on a clean URL), then the app routes to `#/scan/:id`, where
 * progress and the locked report take over.
 *
 * `kind` picks the lane. 'gpc' is the GPC-only lite scan (the signed-out hero
 * form, whose copy describes exactly that check). 'full' is the anonymous full
 * audit, `POST /api/anonymous-audit` (#1777), which past its daily ceiling
 * runs the lite scan instead. The sales report CTA needs 'full': since #1782 a
 * claim starts no scan, so a lite scan there never became the audit the
 * button names. When the server answers with that GPC-only fallback, the
 * visitor goes to the claim URL the www hero uses (#1784), not just the hash.
 *
 * Shared by the signed-out hero form and the sales report CTA, so both fire
 * the same conversion events.
 */
export function useStartLiteScan(kind: 'gpc' | 'full' = 'gpc') {
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** `target` must already have passed validateScanUrl; pass its normalized form. */
  const start = useCallback(async (target: string) => {
    setError(null);
    setIsStarting(true);
    trackScanStarted(target);
    // null means the visitor opted out or is signalling GPC, and neither
    // conversion leg runs.
    const leadEventId = trackLeadSubmitted(target);
    const clickId = leadEventId ? readClickId() : null;

    try {
      const adsLead = leadEventId && clickId ? { leadEventId, clickId } : null;
      const started = kind === 'full'
        ? await startAnonymousAudit(target, adsLead)
        : await startLiteScan(target, 'gpc', adsLead);
      const { scanId, intentToken } = started;
      setScanIntentToken(intentToken);
      setScanIntentId(scanId);
      if ('fallback' in started && started.fallback === 'gpc-only') {
        navigation.replace(claimUrl(scanId, intentToken, target));
        return;
      }
      window.location.hash = `#/scan/${encodeURIComponent(scanId)}`;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start scan');
      setIsStarting(false);
    }
  }, [kind]);

  return { start, isStarting, error, setError };
}
